/**
 * RetentionCurveReport (FEAT-ANALYTICS) — daily user-retention curve for the
 * cohort acquired in the selected window: % still active N days after their first
 * visit, maturity-adjusted (too-recent users excluded from each day's
 * denominator). Complements the weekly cohort grid with a decay curve + Day
 * 1/7/30 milestones.
 */
import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Select, HStack, Stack } from '@chakra-ui/react';
import { useReport } from './api';
import { StatCard, StatGrid, Panel, TrendChart, ReportFrame, fmtInt, fmtPct } from './components';

const RANGES = [[14, '14 days'], [30, '30 days'], [60, '60 days'], [90, '90 days']];

export function RetentionCurveReport({ params }) {
  const [days, setDays] = useState(30);
  const reportParams = useMemo(() => ({ ...params, days }), [params, days]);
  const { data, loading, error } = useReport('retention', reportParams);

  // Drop day 0 (always 100%) from the chart so the decay is readable.
  const curve = (data?.curve || []).filter((c) => c.day >= 1 && c.retention_rate != null);
  const m = data?.milestones || {};

  return (
    <Box>
      <Flex mb={4} align="center" justify="space-between" wrap="wrap" gap={2}>
        <HStack>
          <Text fontSize="sm" color="gray.600">Window</Text>
          <Select size="sm" w="auto" bg="white" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </HStack>
      </Flex>

      <ReportFrame loading={loading} error={error}>
        {data && (
          <Stack spacing={4}>
            <StatGrid columns={{ base: 2, md: 4 }}>
              <StatCard label="Cohort size" value={fmtInt(data.cohort_size)} help="New users acquired in range" />
              <StatCard label="Day 1 retention" value={m.day1 != null ? fmtPct(m.day1) : '—'} />
              <StatCard label="Day 7 retention" value={m.day7 != null ? fmtPct(m.day7) : '—'} />
              <StatCard label="Day 30 retention" value={m.day30 != null ? fmtPct(m.day30) : '—'} />
            </StatGrid>

            <Panel title="User retention curve">
              <Text fontSize="xs" color="gray.500" mb={3}>
                % of the acquired cohort active N days after their first visit. Each day only counts users who have had that many days to return.
              </Text>
              <TrendChart
                categories={curve.map((c) => `Day ${c.day}`)}
                series={[{ name: 'Retention %', data: curve.map((c) => c.retention_rate) }]}
              />
            </Panel>
          </Stack>
        )}
      </ReportFrame>
    </Box>
  );
}

export default RetentionCurveReport;
